/* SIZESOFTX
=========================================================================================
* Descripción: Menú de acciones para entrenamiento IA
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
*      2       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Compatibilidad Android para carga de foto con cámara
*      3       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Carga de foto diferenciada para PC y móvil
*      4       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Acciones de captura y entrenamiento por muestras
* ---------------------------------------------------------------------------------------
*/

import { Fragment } from 'react';
import { useNavigate } from "react-router-dom";
import { IconButton, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import SendIcon from '@mui/icons-material/Send';
import BurstModeIcon from '@mui/icons-material/BurstMode';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import { useIAEntrenamiento } from "../../context/IA/IAEntrenamientoContext";
import { useDoubleClickPrevention } from "../../hooks/useDoubleClickPrevention";

export default function MnuIAEntrenamientoActions({
    onAbrirBusquedaFoto,
    onEntrenar,
    onEntrenarMuestras,
    onEntrenarModelo,
    onEliminarEntrenamiento,
    onCambiarCamara,
    entrenamientoActivo = false,
    disabledEntrenar = false,
    disabledDelete = false,
}) {
    const navigate = useNavigate();
    
    const { 
        handleAbrirBuscarArticulo, 
        handleObtenerInfoAprendizaje,
        articuloSeleccionado,
    } = useIAEntrenamiento();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const handleRegresarDashboard = () => {
        navigate("/");
    };

    const handleRefresh = async () => {
        if (articuloSeleccionado?.clavearticulo) {
            await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        }
    };

    return (
        <Fragment>
            <Tooltip title="Regresar al Dashboard">
                <IconButton onClick={handleRegresarDashboard}>
                    <HomeIcon className="icon" /><p className="icon">Salir</p>
                </IconButton>
            </Tooltip>
            <Tooltip title="Buscar Artículo (Alt+F1)">
                <IconButton onClick={handleAbrirBuscarArticulo}>
                    <SearchIcon className="icon" /><p className="icon">Buscar</p>
                </IconButton>
            </Tooltip>
            <Tooltip title="Buscar por Foto (Reconocimiento IA)">
                <IconButton onClick={() => onAbrirBusquedaFoto && executeWithProtection(onAbrirBusquedaFoto)} disabled={isProcessing || !onAbrirBusquedaFoto}>
                    <ImageSearchIcon className="icon" /><p className="icon">Por Foto</p>
                </IconButton>
            </Tooltip>
            {onCambiarCamara && (
                <Tooltip title="Cambiar cámara">
                    <IconButton onClick={() => executeWithProtection(onCambiarCamara)} disabled={isProcessing || entrenamientoActivo}>
                        <FlipCameraIosIcon className="icon" /><p className="icon">Cámara</p>
                    </IconButton>
                </Tooltip>
            )}
            {onEntrenar && (
                <Tooltip title="Enviar muestra a entrenamiento IA">
                    <IconButton onClick={() => executeWithProtection(onEntrenar)} disabled={isProcessing || disabledEntrenar}>
                        <SendIcon className="icon" /><p className="icon">Entrenar</p>
                    </IconButton>
                </Tooltip>
            )}
            {onEntrenarMuestras && (
                <Tooltip title="Entrenar con 10 muestras durante 10 segundos">
                    <IconButton onClick={() => executeWithProtection(onEntrenarMuestras)} disabled={isProcessing || disabledEntrenar || entrenamientoActivo}>
                        <BurstModeIcon className="icon" /><p className="icon">10 muestras</p>
                    </IconButton>
                </Tooltip>
            )}
            {onEntrenarModelo && (
                <Tooltip title="Entrenar la red neuronal con todo lo aprendido">
                    <IconButton onClick={() => executeWithProtection(onEntrenarModelo)} disabled={isProcessing || entrenamientoActivo}>
                        <AutoAwesomeIcon className="icon" /><p className="icon">Entrenar red</p>
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Refrescar información de aprendizaje">
                <IconButton onClick={() => executeWithProtection(handleRefresh)} disabled={isProcessing}>
                    <RefreshIcon className="icon" />
                </IconButton>
            </Tooltip>
            {onEliminarEntrenamiento && (
                <Tooltip title="Eliminar aprendizaje del artículo seleccionado">
                    <IconButton onClick={() => executeWithProtection(onEliminarEntrenamiento)} disabled={isProcessing || disabledDelete} color="error">
                        <DeleteIcon className="icon" />
                    </IconButton>
                </Tooltip>
            )}
        </Fragment>
    );
}
